import React, { useEffect, useState } from 'react';
import {
    ArrowLeftOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DollarOutlined,
    EditOutlined,
    FileTextOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    SendOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Empty,
    message,
    Progress,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Timeline,
    Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { mapZnsAPIWithErrorHandling } from '../../../api/zns/zns';
import { campainAPIWithErrorHandling } from '../../../api/collection/campain';
import { confirmDelete } from '../../../components/common/confirmDialog';

const { Title, Text } = Typography;

export default function ParentCampainDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    // const [statsLoading, setStatsLoading] = useState(false);
    const [campain, setCampain] = useState(null);
    const [stats, setStats] = useState({});
    const [mockUnitStats, setMockUnitStats] = useState([]);
    const [sendOneLoading, setSendOneLoading] = useState(null);
    const [sendLoading, setSendLoading] = useState(false);

    // Fetch campain data on component mount
    useEffect(() => {
        if (id) {
            fetchCollectionData();
            fetchCollectionStats();
        }
    }, [id]);

    const fetchCollectionData = async () => {
        try {
            setLoading(true);
            const response = await campainAPIWithErrorHandling.getCampainById(
                id
            );
            const collectionData = response.data?.data;

            // if (!collectionData) {
            //     message.error("Không tìm thấy chiến dịch!");
            //     navigate("/campains");
            //     return;
            // }

            setCampain(collectionData);
            setMockUnitStats(collectionData.children);
        } catch (error) {
            message.error("Không thể tải thông tin chiến dịch: " + error.message);
            navigate("/campains");
        } finally {
            setLoading(false);
        }
    };

    const fetchCollectionStats = async () => {
        try {
            // setStatsLoading(true);
            const response = await campainAPIWithErrorHandling.getCampainStats(
                id
            );
            const statsData = response.data?.data;

            if (statsData) {
                setStats({
                    totalCustomers: statsData.total_customers || 0,
                    totalAmountPaid: parseFloat(statsData.total_amount_paid || 0),
                    totalPending: statsData.total_pending || 0,
                    totalPaid: statsData.total_paid || 0,
                    totalOverdue: statsData.total_overdue || 0,
                    collectionRate: statsData.collection_rate || 0,
                    collectionOrg: statsData.total_organizations || 0,
                });
            }
        } catch (error) {
            console.error("Không thể tải thống kê:", error.message);
        } finally {
            // setStatsLoading(false);
        }
    };

    const getStatusInfo = () => {
        if (!campain) return { status: "default", text: "Không xác định", color: "gray" };

        const statusConfig = {
            PLANNED: {
                icon: <ClockCircleOutlined />,
                color: 'blue',
                text: 'Nháp',
            },
            ONGOING: {
                icon: <CheckCircleOutlined />,
                color: 'green',
                text: 'Đang diễn ra',
            },
            CLOSED: {
                icon: <CheckCircleOutlined />,
                color: 'red',
                text: 'Đã kết thúc',
            },
        };
        return statusConfig[campain.status];
    };

    const handleViewDetail = (record) => {
        message.info(`Chi tiết thông tin chiến dịch: ${record.name}`);
        navigate(`/campains/information/${record.id}`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("vi-VN", {
            style: "currency",
            currency: "VND",
        }).format(amount);
    };

    const getChildCollectionStats = (childRecord) => {
        if (!childRecord.customer_fees || childRecord.customer_fees.length === 0) {
            return {
                totalCustomers: 0,
                paidCustomers: 0,
                paidAmount: 0,
                organizationName: childRecord.organization?.name || 'N/A'
            };
        }

        const uniqueCustomers = new Set();
        const paidCustomers = new Set();
        let paidAmount = 0;

        childRecord.customer_fees.forEach(fee => {
            uniqueCustomers.add(fee.customer_id);
            paidAmount += parseFloat(fee.amount_paid || 0);

            if (fee.status === 'PAID' || fee.amount_paid > 0) {
                paidCustomers.add(fee.customer_id);
            }
        });

        const rate = round((paidCustomers.size / uniqueCustomers.size) * 100, 2) || 0;

        return {
            totalCustomers: uniqueCustomers.size,
            paidCustomers: paidCustomers.size,
            rate,
            paidAmount,
            organizationName: childRecord.organization?.name || 'N/A'
        };
    };

    // gửi tin
    const sendOneCollection = async (record) => {
        try {
            setSendOneLoading(record.id);
            const response = await mapZnsAPIWithErrorHandling.sendAllCampaignZns(record.id);
        
            message.success(response?.message ?? "Gửi tin nhắc nhở qua ZNS thành công");
        } catch (error) {
            message.error(error.message);
        } finally {
            setSendOneLoading(null);
        }
    };

    const sendAllParent = async (id) => {
        try {
            setSendLoading(true);
            const response = await mapZnsAPIWithErrorHandling.sendAllParentCampaign(id);
        
            message.success(response?.message ?? "Gửi tin nhắc nhở qua ZNS thành công");
        } catch (error) {
            message.error(error.message);
        } finally {
            setSendLoading(false);
        }
    };

    // Table columns
    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 60,
            align: 'center',
            render: (_, __, index) => <Text>{index + 1}</Text>,
        },
        {
            title: 'Đơn vị',
            key: 'unit',
            width: "15%",
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div>
                        <Text strong>{stats.organizationName}</Text>
                    </div>
                );
            },
        },
        {
            title: 'Tổng KH',
            key: 'total',
            width: "15%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return <Text strong>{stats.totalCustomers}</Text>;
            },
        },
        {
            title: 'Đã thu',
            key: 'paid',
            width: "15%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return <Text type="success" strong>{stats.paidCustomers}</Text>;
            },
        },
        {
            title: 'Tỷ lệ thu',
            key: 'collection_rate',
            width: "20%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div>
                        <Text strong className="text-purple-600">
                            {stats.rate}%
                        </Text>
                        <Progress
                            percent={stats.rate}
                            strokeColor="#757FE7"
                            size="small"
                            className="mt-1"
                            showInfo={false}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Số tiền đã thu',
            key: 'amount',
            width: "20%",
            align: 'right',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ color: '#128555' }}>
                            {formatCurrency(stats.paidAmount)}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'Hành động',
            key: 'actions',
            width: "15%",
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="default"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => handleViewDetail(record)}
                        title="Xem chi tiết"
                    />
                    <Button
                        type="default"
                        size="small"
                        icon={<SendOutlined />}
                        onClick={() => sendOneCollection(record)}
                        title="Gửi tin nhắc thanh toán"
                        loading={sendOneLoading === record.id || sendLoading === true}
                        disabled={["PAID", "CANCELLED"].includes(record.status) || campain.status === "PLANNED"}
                    />
                </Space>
            ),
        },
    ];


    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    height: "80vh",
                    alignItems: "center",
                }}>
                <Spin size="large" tip="Đang tải thông tin chiến dịch..." />
            </div>
        );
    }

    if (!campain) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center">
                <Empty description="Không tìm thấy chiến dịch" />
            </div>
        );
    }

    const statusInfo = getStatusInfo();

    // Action


    const handleEdit = () => {
        navigate(`/campains/${id}`);
    };

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete({ entity: "chiến dịch", name: campain.name });
            if (!confirmed) return;

            await campainAPIWithErrorHandling.deleteCampain(campain.id);
            message.success("Xóa chiến dịch thành công");

            // Refresh data
            navigate("/campains");
        } catch (error) {
            message.error(error.message || "Không thể xóa chiến dịch");
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
            <Card className="shadow-lg mx-auto">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        items={[
                            {
                                title: (
                                    <>
                                        <HomeOutlined /> Trang chủ
                                    </>
                                ),
                            },
                            {
                                title: (
                                    <>
                                        <DollarOutlined /> Chiến dịch thu
                                    </>
                                ),
                            },
                            {
                                title: (
                                    <>
                                        <InfoCircleOutlined /> Chi tiết tổng hợp
                                    </>
                                ),
                            },
                        ]}
                    />
                </div>

                {/* Header */}
                <div className="mb-6">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space align="start" direction="vertical" size={4}>
                                <div>
                                    <Title level={2} className="mb-2">
                                        {campain.name}
                                    </Title>
                                    <Space size={8}>
                                        <Tag color="purple" className="px-3 py-1"
                                            style={{ fontSize: '14px' }}
                                        >
                                            <TeamOutlined /> Chiến dịch tổng hợp
                                        </Tag>
                                        <Tag
                                            color={statusInfo.color}
                                            icon={statusInfo.icon}
                                            className="px-3 py-1"
                                        >
                                            {statusInfo.text}
                                        </Tag>
                                    </Space>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    size="default"
                                    className="shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                    disabled={campain.status !== 'PLANNED'}
                                >
                                    Chỉnh sửa
                                </Button>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    className="shadow-sm"
                                >
                                    Xóa
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <Divider />

                {/* BLOCK 1: Overview Statistics */}
                <Row gutter={[24, 16]} className="mb-6">
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="shadow-sm border-purple-200">
                            <Statistic
                                title="Tổng đơn vị"
                                value={stats.collectionOrg || 0}
                                prefix={<TeamOutlined className="text-purple-500" />}
                                suffix="đơn vị"
                                valueStyle={{ color: '#656DC7' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="shadow-sm border-blue-200">
                            <Statistic
                                title="Tổng khách hàng"
                                value={stats.totalCustomers || 0}
                                prefix={<TeamOutlined className="text-blue-500" />}
                                suffix="khách"
                                valueStyle={{ color: '#0070D7' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="shadow-sm border-green-200">
                            <Statistic
                                title="Đã ủng hộ"
                                value={stats.totalPaid}
                                prefix={<TeamOutlined />}
                                valueStyle={{ color: '#3209e7'}}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Card className="shadow-sm border-orange-200">
                            <Statistic
                                title="Đã nhận"
                                value={stats.totalAmountPaid}
                                formatter={(value) => formatCurrency(value)}
                                prefix={<DollarOutlined />}
                                valueStyle={{ color: '#A8681E', fontSize: '20px' }}
                            />
                            <Progress
                                percent={stats.collectionRate || 0}
                                strokeColor="#16A66A"
                                className="mt-2"
                                size="small"
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Thông tin chiến dịch và Timeline */}
                <Row gutter={[24, 16]} className="mb-6">
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <span className="text-blue-600" style={{ color: '#667eea' }}>
                                    <CalendarOutlined className="mr-2" />
                                    Thông tin chiến dịch
                                </span>
                            }
                            className="shadow-sm border-blue-200 h-full"
                        >
                            <Descriptions bordered column={2} size="middle">
                                <Descriptions.Item span={2} label="Tên chiến dịch">
                                    <Text  strong className="text-gray-800">{campain.name}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Mã chiến dịch">
                                    <Tag color="blue" className="text-sm px-3 py-1">{campain.code}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="T/K phát hành hóa đơn">
                                    <Space>
                                        <Text className="text-gray-800" strong>
                                            {campain?.invoice_cnf?.name}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Ngày bắt đầu">
                                    <Space>
                                        <CalendarOutlined className="text-blue-500" />
                                        <Text className="text-gray-800">
                                            {dayjs(campain.start_date).format("DD/MM/YYYY")}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Ngày kết thúc">
                                    <Space>
                                        <CalendarOutlined />
                                        <Text className="text-gray-800">
                                            {dayjs(campain.end_date).format("DD/MM/YYYY")}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Thời gian diễn ra">
                                    <Text>
                                        {dayjs(campain.end_date).diff(
                                            dayjs(campain.start_date),
                                            "day"
                                        ) + 1}{" "}
                                        ngày
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Giờ gửi tin nhắc">
                                    <Space>
                                        <ClockCircleOutlined className="text-purple-500" />
                                        <Text className="text-gray-800">
                                            {campain.send_schedule_at
                                                ? dayjs(campain.send_schedule_at, "HH:mm:ss").format("HH:mm:ss")
                                                : "—"}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Sau hạn">
                                    <Text>{campain.after_date ?? "—"}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Trước hạn">
                                    <Text>{campain.before_date ?? "—"}</Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card
                            title={
                                <span className="text-purple-600"  style={{ color: '#764ba2' }}>
                                    <ClockCircleOutlined className="mr-2" />
                                    Lịch sử thay đổi
                                </span>
                            }
                            className="shadow-sm border-purple-200 h-full"
                        >
                            <Timeline>
                                <Timeline.Item color="green" dot={<CheckCircleOutlined style={{ fontSize: '16px' }} />}>
                                    <Text strong>Tạo chiến dịch</Text>
                                    <br />
                                    <Text type="secondary">
                                        {new Date(campain.created_at).toLocaleString('vi-VN')}
                                    </Text>
                                </Timeline.Item>

                                {campain.updated_at !== campain.created_at && (
                                    <Timeline.Item color="blue" dot={<EditOutlined style={{ fontSize: '16px' }} />}>
                                        <Text strong>Cập nhật lần cuối</Text>
                                        <br />
                                        <Text type="secondary">
                                            {new Date(campain.updated_at).toLocaleString('vi-VN')}
                                        </Text>
                                    </Timeline.Item>
                                )}

                                <Timeline.Item color={statusInfo.color} dot={statusInfo.icon}>
                                    <Text strong>Trạng thái hiện tại</Text>
                                    <br />
                                    <Tag color={statusInfo.color} style={{ marginTop: '4px' }}>
                                        {statusInfo.text}
                                    </Tag>
                                </Timeline.Item>
                            </Timeline>
                        </Card>
                    </Col>
                </Row>

                {/* Content Card */}
                <Card
                    title={
                        <>
                        <FileTextOutlined className="mr-2" />
                        Nội dung tin nhắn
                        </>
                    }
                    className="shadow-sm mb-6"
                    >
                    {campain.content ? (
                        <div
                        className="mb-0 p-3 bg-gray-50 rounded"
                        dangerouslySetInnerHTML={{ __html: campain.content }}
                        style={{
                            lineHeight: 1.8,
                        }}
                        />
                    ) : (
                        <Text type="secondary">Chưa có nội dung</Text>
                    )}
                </Card>

                {/* BLOCK 2: Tiến độ theo đơn vị (Table) */}
                <Card
                    className="shadow-md border-0 rounded-xl"
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-orange-600 font-semibold" style={{ color: '#f5576c' }}>
                                <FileTextOutlined className="mr-2" />
                                Tiến độ theo đơn vị 
                                
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={() => sendAllParent(campain.id)}
                                    loading={sendLoading}
                                    style={{marginLeft : "10px"}}
                                    disabled={campain.status === "PLANNED"}
                                >
                                    Gửi tất cả
                                </Button>
                            </span>
                            <Text type="secondary" style={{ fontSize: 14 }}>
                                Tổng: <strong>{stats.collectionOrg}</strong> đơn vị
                            </Text>
                        </div>
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={mockUnitStats}
                        rowKey="id"
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={false}
                        className="rounded-lg"
                        rowClassName="hover:bg-blue-50/50 transition-colors"
                    />
                </Card>

                {/* Action Buttons */}
                <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-purple-50">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => alert('Quay lại danh sách chiến dịch')}
                                size="default"
                            >
                                Quay lại
                            </Button>
                        </Col>
                        <Col>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    size="default"
                                    className="shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                    disabled={campain.status !== 'PLANNED'}
                                >
                                    Chỉnh sửa
                                </Button>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    className="shadow-sm"
                                >
                                    Xóa
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Card>
        </div>
    );
}
